import React, { useState, useEffect, useCallback } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaCalendarCheck,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaArrowLeft,
  FaVideo,
} from "react-icons/fa";
import {
  saveAppointment,
  getBookedSlots,
  getAvailabilitySettings,
  getBatchCalendarDataCached,
  performanceMonitor,
  clearCalendarCache,
} from "../utils/emailUtils";
import {
  getBlockedDates,
  getFomoSettings,
  getAppointmentCount,
} from "../firebase";
import "./CalendlyCalendar.css";

const CalendlyCalendar = ({ userInfo, onBack, onComplete }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]); // eslint-disable-line no-unused-vars
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [step, setStep] = useState("calendar"); // 'calendar', 'time', 'confirm'
  const [availabilitySettings, setAvailabilitySettings] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [fomoSettings, setFomoSettings] = useState(null);
  const [appointmentCount, setAppointmentCount] = useState(null);

  // Default time slots (will be overridden by admin settings)
  const defaultTimeSlots = [
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
  ];

  const months = [
    "Ocak",
    "Şubat",
    "Mart",
    "Nisan",
    "Mayıs",
    "Haziran",
    "Temmuz",
    "Ağustos",
    "Eylül",
    "Ekim",
    "Kasım",
    "Aralık",
  ];

  // Monday-first, matching the grid ordering in getDaysInMonth
  const days = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

  const loadAvailabilityData = useCallback(async () => {
    setIsInitialLoading(true);
    performanceMonitor.startTiming("loadAvailabilityData");

    try {
      console.log("🚀 USING FRESH DATA (no cache) for initial calendar load");

      // Clear cache to ensure fresh data
      clearCalendarCache();

      // Use the new optimized batch API without cache
      const batchResult = await getBatchCalendarDataCached(null, 30);
      const { data } = batchResult;

      setAvailabilitySettings(data.availabilitySettings);
      setBlockedDates(data.blockedDates);
      setFomoSettings(data.fomoSettings);

      // Load appointment count if FOMO is enabled and counter is enabled
      if (
        data.fomoSettings?.enabled &&
        data.fomoSettings?.showAppointmentCounter
      ) {
        try {
          const count = await getAppointmentCount();
          setAppointmentCount(count);
        } catch (error) {
          console.error("Error loading appointment count:", error);
        }
      }

      console.log("✅ Batch calendar data loaded successfully", {
        availabilitySettings: !!data.availabilitySettings,
        blockedDatesCount: data.blockedDates?.length || 0,
        fomoSettings: !!data.fomoSettings,
      });
    } catch (error) {
      console.error(
        "❌ Error loading batch availability data, falling back to individual calls:",
        error
      );

      // Fallback to original method if batch fails
      try {
        const [settings, blocked, fomo] = await Promise.all([
          getAvailabilitySettings(),
          getBlockedDates(),
          getFomoSettings(),
        ]);
        setAvailabilitySettings(settings);
        setBlockedDates(blocked);
        setFomoSettings(fomo);

        if (fomo?.enabled && fomo?.showAppointmentCounter) {
          try {
            const count = await getAppointmentCount();
            setAppointmentCount(count);
          } catch (error) {
            console.error("Error loading appointment count:", error);
          }
        }
      } catch (fallbackError) {
        console.error(
          "❌ Fallback also failed, using defaults:",
          fallbackError
        );
        // Use default settings if all loading fails
        setAvailabilitySettings({
          workingDays: [1, 2, 3, 4, 5],
          timeSlots: defaultTimeSlots,
          customTimeSlots: {},
          blockedTimeSlots: {},
        });
        setFomoSettings({
          enabled: false,
          hidePercentage: 30,
          upcomingDaysOnly: true,
          showFomoIndicators: true,
          showAppointmentCounter: true,
          popularHours: ["10:00", "11:00", "14:00", "15:00"],
          lowSlotsThreshold: 3,
        });
      }
    }

    performanceMonitor.endTiming("loadAvailabilityData");
    setIsInitialLoading(false);
  }, []);

  const loadBookedSlots = useCallback(async () => {
    if (!selectedDate || !availabilitySettings) return;

    setIsLoadingTimeSlots(true);
    performanceMonitor.startTiming("loadBookedSlots");

    try {
      // Fix timezone issue: Use local date instead of UTC date
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const selectedDateStr = `${year}-${month}-${day}`;

      console.log(
        "🚀 REAL-TIME DATA: Loading fresh data for selected date:",
        selectedDateStr
      );

      // ALWAYS clear cache before loading booked slots to ensure real-time data
      clearCalendarCache();

      let booked = [];
      try {
        // Fresh booked slots straight from the API (cache already cleared above)
        booked = await getBookedSlots(selectedDateStr);
        console.log("✅ Got fresh booked slots from API:", booked.length);
      } catch (apiError) {
        console.log("⚠️ Booked slots API call failed, using empty array:", apiError.message);
        booked = [];
      }

      setBookedSlots(booked);

      // Get time slots for this date - use same local date format
      const blockedSlotsForDate =
        availabilitySettings.blockedTimeSlots?.[selectedDateStr] || [];

      // Filter available slots: remove booked and blocked slots
      let availableTimeSlots =
        availabilitySettings.timeSlots || defaultTimeSlots;

      // Filter out booked and blocked slots
      let available = availableTimeSlots.filter(
        (slot) => !booked.includes(slot) && !blockedSlotsForDate.includes(slot)
      );

      // If selected date is today, filter out past time slots using Turkey time (UTC+3)
      const today = new Date();
      const turkeyTime = new Date(today.getTime() + 3 * 60 * 60 * 1000); // Add 3 hours for Turkey time
      const selectedDateTurkey = new Date(
        selectedDate.getTime() + 3 * 60 * 60 * 1000
      );

      // Check if selected date is today in Turkey time
      const isTodayInTurkey =
        selectedDateTurkey.getUTCFullYear() === turkeyTime.getUTCFullYear() &&
        selectedDateTurkey.getUTCMonth() === turkeyTime.getUTCMonth() &&
        selectedDateTurkey.getUTCDate() === turkeyTime.getUTCDate();

      if (isTodayInTurkey) {
        const currentHour = turkeyTime.getUTCHours();
        const currentMinute = turkeyTime.getUTCMinutes();
        const currentTimeInMinutes = currentHour * 60 + currentMinute;

        available = available.filter((slot) => {
          const [hours, minutes] = slot.split(":").map(Number);
          const slotTimeInMinutes = hours * 60 + minutes;
          return slotTimeInMinutes > currentTimeInMinutes;
        });
      }

      // Sort available slots chronologically to ensure correct order
      available.sort((a, b) => {
        const [aHours, aMinutes] = a.split(":").map(Number);
        const [bHours, bMinutes] = b.split(":").map(Number);
        const aTimeInMinutes = aHours * 60 + aMinutes;
        const bTimeInMinutes = bHours * 60 + bMinutes;
        return aTimeInMinutes - bTimeInMinutes;
      });

      // Apply FOMO logic (fake busy slots)
      const finalAvailable = applyFomoLogic(available, selectedDate);
      setAvailableSlots(finalAvailable);
    } catch (error) {
      console.error("Error loading booked slots:", error);
      const fallbackSlots = availabilitySettings?.timeSlots || defaultTimeSlots;
      // Sort fallback slots as well
      const sortedFallbackSlots = [...fallbackSlots].sort((a, b) => {
        const [aHours, aMinutes] = a.split(":").map(Number);
        const [bHours, bMinutes] = b.split(":").map(Number);
        const aTimeInMinutes = aHours * 60 + aMinutes;
        const bTimeInMinutes = bHours * 60 + bMinutes;
        return aTimeInMinutes - bTimeInMinutes;
      });
      setAvailableSlots(sortedFallbackSlots);
    }

    performanceMonitor.endTiming("loadBookedSlots");
    setIsLoadingTimeSlots(false);
  }, [selectedDate, availabilitySettings, fomoSettings]);

  // useEffect hooks
  useEffect(() => {
    loadAvailabilityData();
  }, [loadAvailabilityData]);

  useEffect(() => {
    if (selectedDate && availabilitySettings) {
      // Load fresh data when date changes - cache clearing happens in loadBookedSlots
      loadBookedSlots();
    }
  }, [selectedDate, availabilitySettings, loadBookedSlots]);

  // CRITICAL FEATURE: Real-time listener for appointments
  // This ensures that when ANY user books an appointment, ALL other users see the update IMMEDIATELY
  useEffect(() => {
    if (!selectedDate) return;

    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const selectedDateStr = `${year}-${month}-${day}`;

    console.log("🎯 REAL-TIME LISTENER: Setting up real-time listener for date:", selectedDateStr);

    let cancelled = false;

    // Near-real-time updates via short-interval polling (replaces Firestore onSnapshot).
    const refresh = async () => {
      let realTimeBookedSlots = [];
      try {
        realTimeBookedSlots = await getBookedSlots(selectedDateStr);
      } catch (error) {
        console.error("❌ Booked-slots poll failed:", error);
        return;
      }
      if (cancelled) return;

      {
        {
          // Update booked slots
          setBookedSlots(realTimeBookedSlots);

          // Immediately update available slots
          if (availabilitySettings) {
            const blockedSlotsForDate = availabilitySettings.blockedTimeSlots?.[selectedDateStr] || [];
            let availableTimeSlots = availabilitySettings.timeSlots || defaultTimeSlots;

            // Filter out booked and blocked slots
            let available = availableTimeSlots.filter(
              (slot) => !realTimeBookedSlots.includes(slot) && !blockedSlotsForDate.includes(slot)
            );

            // Apply time filtering if today
            const today = new Date();
            const turkeyTime = new Date(today.getTime() + 3 * 60 * 60 * 1000);
            const selectedDateTurkey = new Date(selectedDate.getTime() + 3 * 60 * 60 * 1000);
            const isTodayInTurkey =
              selectedDateTurkey.getUTCFullYear() === turkeyTime.getUTCFullYear() &&
              selectedDateTurkey.getUTCMonth() === turkeyTime.getUTCMonth() &&
              selectedDateTurkey.getUTCDate() === turkeyTime.getUTCDate();

            if (isTodayInTurkey) {
              const currentHour = turkeyTime.getUTCHours();
              const currentMinute = turkeyTime.getUTCMinutes();
              const currentTimeInMinutes = currentHour * 60 + currentMinute;

              available = available.filter((slot) => {
                const [hours, minutes] = slot.split(":").map(Number);
                const slotTimeInMinutes = hours * 60 + minutes;
                return slotTimeInMinutes > currentTimeInMinutes;
              });
            }

            // Sort available slots
            available.sort((a, b) => {
              const [aHours, aMinutes] = a.split(":").map(Number);
              const [bHours, bMinutes] = b.split(":").map(Number);
              const aTimeInMinutes = aHours * 60 + aMinutes;
              const bTimeInMinutes = bHours * 60 + bMinutes;
              return aTimeInMinutes - bTimeInMinutes;
            });

            // Apply FOMO logic and update
            const finalAvailable = applyFomoLogic(available, selectedDate);
            setAvailableSlots(finalAvailable);

            console.log("🎯 REAL-TIME UPDATE COMPLETE: Available slots updated to", finalAvailable.length, "slots");
          }
        }
      }
    };

    // Poll immediately, then every 20s for near-real-time updates.
    refresh();
    const intervalId = setInterval(refresh, 20000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [selectedDate, availabilitySettings, fomoSettings]);

  // FOMO Logic: Hide some available slots to create fake scarcity
  const applyFomoLogic = (availableSlots, selectedDate) => {
    if (!fomoSettings || !fomoSettings.enabled || availableSlots.length === 0) {
      return availableSlots;
    }

    // Check if we should apply FOMO only to upcoming days
    if (fomoSettings.upcomingDaysOnly) {
      const today = new Date();
      const turkeyTime = new Date(today.getTime() + 3 * 60 * 60 * 1000);
      const daysDifference = Math.ceil(
        (selectedDate - turkeyTime) / (1000 * 60 * 60 * 24)
      );

      // Only apply to next 3 days
      if (daysDifference > 3) {
        return availableSlots;
      }
    }

    const hideCount = Math.floor(
      (availableSlots.length * fomoSettings.hidePercentage) / 100
    );

    if (hideCount === 0) {
      return availableSlots;
    }

    // Create a deterministic but seemingly random selection based on date
    // This ensures the same slots are hidden for the same date consistently
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const dateString = `${year}-${month}-${day}`;
    const seed = dateString
      .split("-")
      .reduce((acc, part) => acc + parseInt(part), 0);

    // Use seeded random to select slots to hide
    const slotsToHide = [];
    const availableCopy = [...availableSlots];

    for (let i = 0; i < hideCount; i++) {
      if (availableCopy.length === 0) break;

      // Create pseudo-random index based on seed and iteration
      const randomIndex = (seed * (i + 1) * 17) % availableCopy.length;
      const slotToHide = availableCopy.splice(randomIndex, 1)[0];
      slotsToHide.push(slotToHide);
    }

    return availableCopy;
  };

  // Get FOMO indicators for a slot
  const getFomoIndicators = (slot, availableSlots) => {
    if (!fomoSettings || !fomoSettings.showFomoIndicators) {
      return null;
    }

    const indicators = [];

    // Check if it's a popular hour
    if (fomoSettings.popularHours.includes(slot)) {
      indicators.push({
        type: "popular",
        text: "🔥 Popüler saat!",
        className: "fomo-popular",
      });
    }

    // Check if slots are running low
    if (availableSlots.length <= fomoSettings.lowSlotsThreshold) {
      indicators.push({
        type: "low",
        text: "⚡ Az slot kaldı!",
        className: "fomo-low",
      });
    }

    return indicators.length > 0 ? indicators : null;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek =
      firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const isDateAvailable = (date) => {
    if (!date || !availabilitySettings) return false;

    // Use Turkey time (UTC+3) for date comparison
    const now = new Date();
    const turkeyTime = new Date(now.getTime() + 3 * 60 * 60 * 1000);
    const today = new Date(
      turkeyTime.getUTCFullYear(),
      turkeyTime.getUTCMonth(),
      turkeyTime.getUTCDate()
    );

    // Check if date is in the future or today
    if (date < today) return false;

    // Check 2-week limit using Turkey time
    const twoWeeksFromNow = new Date(turkeyTime.getTime());
    twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
    if (date > twoWeeksFromNow) return false;

    // Check if day is in working days
    const dayOfWeek = date.getDay();
    if (!availabilitySettings.workingDays.includes(dayOfWeek)) return false;

    // Check if date is blocked - use local date format
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const dateString = `${year}-${month}-${day}`;
    const isBlocked = blockedDates.some((blocked) => {
      let blockedDate;
      if (blocked.date?.seconds) {
        // Firestore timestamp
        blockedDate = new Date(blocked.date.seconds * 1000);
      } else if (blocked.date) {
        // Regular date
        blockedDate = new Date(blocked.date);
      } else {
        return false;
      }
      const blockedYear = blockedDate.getFullYear();
      const blockedMonth = String(blockedDate.getMonth() + 1).padStart(2, "0");
      const blockedDay = String(blockedDate.getDate()).padStart(2, "0");
      const blockedDateString = `${blockedYear}-${blockedMonth}-${blockedDay}`;
      return blockedDateString === dateString;
    });

    return !isBlocked;
  };

  const formatDate = (date) => {
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    // Handle both Date object and string formats
    const dateObject = typeof date === "string" ? new Date(date) : date;
    return dateObject.toLocaleDateString("tr-TR", options);
  };

  const goToPreviousMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  const handleDateSelect = (date) => {
    if (isDateAvailable(date)) {
      setSelectedDate(date);
      setSelectedTime(null);
      setStep("time");
    }
  };

  // Function to revert optimistic UI updates when user cancels selection
  const revertOptimisticUpdate = (time) => {
    if (!time) return;

    console.log("🔄 Reverting optimistic update for slot:", time);

    // Add the slot back to available slots
    setAvailableSlots((prevSlots) => {
      if (!prevSlots.includes(time)) {
        const revertedSlots = [...prevSlots, time].sort((a, b) => {
          const [aHours, aMinutes] = a.split(":").map(Number);
          const [bHours, bMinutes] = b.split(":").map(Number);
          return (aHours * 60 + aMinutes) - (bHours * 60 + bMinutes);
        });
        console.log(`✅ Reverted slot ${time} back to available slots`);
        return revertedSlots;
      }
      return prevSlots;
    });

    // Remove from booked slots since user cancelled
    setBookedSlots((prevBooked) => {
      const revertedBooked = prevBooked.filter((slot) => slot !== time);
      console.log(`➖ Removed slot ${time} from optimistic booked slots`);
      return revertedBooked;
    });
  };

  const handleTimeSelect = (time) => {
    console.log("🎯 User selected time slot:", time);

    // Optimistic UI update: immediately remove the selected slot from available slots
    // This prevents other users from seeing the same slot while this user is confirming
    setAvailableSlots((prevSlots) => {
      const updatedSlots = prevSlots.filter((slot) => slot !== time);
      console.log(
        `⚡ Optimistically removed slot ${time} from UI. Remaining: ${updatedSlots.length}`
      );
      return updatedSlots;
    });

    // Add the slot to booked slots immediately for UI consistency
    setBookedSlots((prevBooked) => {
      if (!prevBooked.includes(time)) {
        const updatedBooked = [...prevBooked, time];
        console.log(
          `➕ Optimistically added slot ${time} to booked slots. Total: ${updatedBooked.length}`
        );
        return updatedBooked;
      }
      return prevBooked;
    });

    setSelectedTime(time);
    setStep("confirm");

    // Scroll to appointment summary section after a brief delay
    setTimeout(() => {
      const confirmationContainer = document.querySelector('.confirmation-container');
      if (confirmationContainer) {
        confirmationContainer.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }, 100);
  };

  // Enhanced back navigation handlers with optimistic update reversal
  const handleBackToTimeSelection = () => {
    revertOptimisticUpdate(selectedTime);
    setSelectedTime(null);
    setStep("time");
  };

  const handleConfirmAppointment = async () => {
    if (!selectedDate || !selectedTime || !userInfo.userId) return;

    setIsLoading(true);
    setLoadingMessage("Randevunuz kaydediliyor...");

    try {
      // FIRST: Real-time check if slot is still available
      setLoadingMessage("Saat müsaitliği kontrol ediliyor...");

      // Direct API call without cache to get latest booked slots
      // Clear cache first to ensure we get the most up-to-date data
      clearCalendarCache();
      const latestBookedSlots = await getBookedSlots(selectedDate);

      // Check if the selected time is now booked by someone else
      if (latestBookedSlots.includes(selectedTime)) {
        console.log("❌ Slot was taken by another user, reverting optimistic update");

        // Revert the optimistic UI update since slot was taken by someone else
        setAvailableSlots((prevSlots) => {
          if (!prevSlots.includes(selectedTime)) {
            const revertedSlots = [...prevSlots, selectedTime].sort((a, b) => {
              const [aHours, aMinutes] = a.split(":").map(Number);
              const [bHours, bMinutes] = b.split(":").map(Number);
              return (aHours * 60 + aMinutes) - (bHours * 60 + bMinutes);
            });
            console.log(`🔄 Reverted slot ${selectedTime} back to available slots`);
            return revertedSlots;
          }
          return prevSlots;
        });

        // Remove from booked slots since it wasn't successfully booked by this user
        setBookedSlots((prevBooked) => {
          const revertedBooked = prevBooked.filter((slot) => slot !== selectedTime);
          console.log(`➖ Removed slot ${selectedTime} from booked slots`);
          return revertedBooked;
        });

        // Show user-friendly message and go back to time selection
        alert("Bu saat başka bir kullanıcı tarafından alındı. Lütfen başka bir saat seçin.");
        setStep("time");
        setSelectedTime(null);
        // Refresh the available slots to get the latest state
        await loadBookedSlots();
        setIsLoading(false);
        setLoadingMessage("");
        return;
      }
      // Fix timezone issue: Use local date instead of UTC date
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const day = String(selectedDate.getDate()).padStart(2, "0");
      const localDateString = `${year}-${month}-${day}`;

      const appointmentData = {
        date: localDateString, // Store as YYYY-MM-DD format using local timezone
        time: selectedTime,
        userInfo: {
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.email,
          phone: userInfo.phone,
        },
      };

      // Step 1: Save appointment to database
      setLoadingMessage("Randevu bilgileri kaydediliyor...");
      const saveResult = await saveAppointment(userInfo.userId, appointmentData);

      // Get meetLink from response
      const meetLink = saveResult.meetLink || null;
      console.log("Meet link from saveAppointment:", meetLink);

      // Step 2: Clear cache to ensure fresh data on next load
      clearCalendarCache();
      console.log("🧹 Cache cleared after successful appointment booking");

      // Step 3: Confirm optimistic UI updates since booking was successful
      // The slot was already removed from available slots and added to booked slots
      // during optimistic update, so we just need to confirm the state is correct
      console.log(
        `✅ Booking confirmed! Optimistic UI updates are now permanent for slot ${selectedTime}`
      );

      // Step 4: Force refresh of booking data to ensure UI is in sync with database
      // This is a safety measure to handle any edge cases or concurrent bookings
      setTimeout(async () => {
        console.log("🔄 Performing post-booking refresh to verify UI state");
        await loadBookedSlots();
      }, 1000); // Small delay to allow database to propagate

      // Step 5: Complete the process
      setLoadingMessage("İşlem tamamlanıyor...");
      onComplete({
        date: selectedDate,
        time: selectedTime,
        meetLink: meetLink, // Include meetLink in appointment data
        ...appointmentData,
      });
    } catch (error) {
      console.error("Error saving appointment:", error);

      // Revert optimistic UI updates since booking failed
      console.log("❌ Booking failed, reverting optimistic UI updates");
      revertOptimisticUpdate(selectedTime);

      alert("Randevu kaydedilirken bir hata oluştu. Lütfen tekrar deneyin.");

      // Reset state and go back to time selection
      setSelectedTime(null);
      setStep("time");
      setIsLoading(false);
      setLoadingMessage("");

      // Refresh slots to ensure we have the latest state
      await loadBookedSlots();
    }
  };

  const renderCalendar = () => {
    if (isInitialLoading) {
      return (
        <div className="cal-calendar">
          <div className="cal-cal-head">
            <div className="cal-skel cal-skel-title" />
            <div className="cal-nav">
              <div className="cal-skel cal-skel-nav" />
              <div className="cal-skel cal-skel-nav" />
            </div>
          </div>
          <div className="cal-weekdays">
            {days.map((day) => (
              <span key={day} className="cal-weekday">
                {day}
              </span>
            ))}
          </div>
          <div className="cal-grid">
            {Array.from({ length: 35 }).map((_, i) => (
              <div key={i} className="cal-skel cal-day-skel" />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="cal-calendar">
        <div className="cal-cal-head">
          <h3 className="cal-month">
            {months[currentDate.getMonth()]}{" "}
            <span>{currentDate.getFullYear()}</span>
          </h3>
          <div className="cal-nav">
            <button
              className="cal-nav-btn"
              onClick={goToPreviousMonth}
              aria-label="Önceki ay"
            >
              <FaChevronLeft />
            </button>
            <button
              className="cal-nav-btn"
              onClick={goToNextMonth}
              aria-label="Sonraki ay"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        <div className="cal-weekdays">
          {days.map((day) => (
            <span key={day} className="cal-weekday">
              {day}
            </span>
          ))}
        </div>

        <div className="cal-grid">
          {getDaysInMonth(currentDate).map((date, index) => {
            if (!date) {
              return <div key={index} className="cal-day empty" />;
            }
            const available = isDateAvailable(date);
            const isSelected =
              selectedDate &&
              date.toDateString() === selectedDate.toDateString();
            const isToday = date.toDateString() === new Date().toDateString();
            return (
              <button
                key={index}
                type="button"
                className={`cal-day ${available ? "available" : "disabled"} ${
                  isSelected ? "selected" : ""
                } ${isToday ? "today" : ""}`}
                onClick={() => handleDateSelect(date)}
                disabled={!available}
              >
                {date.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTimeSlots = () => (
    <div className="cal-times">
      <div className="cal-times-head">
        <h4>
          <strong>
            {selectedDate.toLocaleDateString("tr-TR", { weekday: "long" })}
          </strong>
          <span>
            {selectedDate.getDate()} {months[selectedDate.getMonth()]}
          </span>
        </h4>
      </div>

      {isLoadingTimeSlots ? (
        <div className="cal-times-list">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="cal-skel cal-slot-skel" />
          ))}
        </div>
      ) : availableSlots.length === 0 ? (
        <div className="cal-no-slots">
          Bu tarih için müsait saat bulunmamaktadır.
        </div>
      ) : (
        <div className="cal-times-list">
          {availableSlots.map((time) => {
            const fomoIndicators = getFomoIndicators(time, availableSlots);
            return (
              <button
                key={time}
                type="button"
                className={`cal-slot ${selectedTime === time ? "selected" : ""}`}
                onClick={() => handleTimeSelect(time)}
              >
                <span className="cal-slot-time">
                  <FaClock className="cal-slot-icon" />
                  {time}
                </span>
                {fomoIndicators && (
                  <span className="cal-slot-badges">
                    {fomoIndicators.map((indicator, index) => (
                      <span
                        key={index}
                        className={`cal-badge ${indicator.className}`}
                      >
                        {indicator.text}
                      </span>
                    ))}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderConfirmation = () => (
    <div className="cal-confirm">
      <div className="cal-confirm-head">
        <FaCalendarCheck className="cal-confirm-icon" />
        <h3>Randevu Özeti</h3>
        <p>Bilgileri kontrol edip randevunuzu onaylayın.</p>
      </div>

      <div className="cal-summary">
        <div className="cal-summary-row">
          <FaCalendarCheck className="cal-summary-icon" />
          <div>
            <span>Tarih</span>
            <p>{formatDate(selectedDate)}</p>
          </div>
        </div>
        <div className="cal-summary-row">
          <FaClock className="cal-summary-icon" />
          <div>
            <span>Saat</span>
            <p>{selectedTime}</p>
          </div>
        </div>
        <div className="cal-summary-row">
          <FaUser className="cal-summary-icon" />
          <div>
            <span>İsim</span>
            <p>
              {userInfo.firstName} {userInfo.lastName}
            </p>
          </div>
        </div>
        <div className="cal-summary-row">
          <FaEnvelope className="cal-summary-icon" />
          <div>
            <span>E-posta</span>
            <p>{userInfo.email}</p>
          </div>
        </div>
        <div className="cal-summary-row">
          <FaPhone className="cal-summary-icon" />
          <div>
            <span>Telefon</span>
            <p>{userInfo.phone}</p>
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="cal-loading-overlay">
          <div className="cal-spinner" />
          <div className="cal-loading-text">{loadingMessage}</div>
        </div>
      )}

      <button
        className="cal-confirm-btn"
        onClick={handleConfirmAppointment}
        disabled={isLoading}
      >
        {isLoading ? loadingMessage : "Randevuyu Onayla"}
      </button>
    </div>
  );

  return (
    <div className="cal-page">
      <div className="cal-card">
        <aside className="cal-aside">
          <button className="cal-back" onClick={onBack}>
            <FaArrowLeft />
            <span>Geri</span>
          </button>

          <div className="cal-avatar">
            <FaVideo />
          </div>
          <span className="cal-eyebrow">Ücretsiz online görüşme</span>
          <h2 className="cal-title">Danışmanlık Randevusu</h2>

          <ul className="cal-info">
            <li>
              <FaClock />
              <span>30 dakika</span>
            </li>
            <li>
              <FaVideo />
              <span>Google Meet üzerinden</span>
            </li>
            {selectedDate && (
              <li className="cal-info-selected">
                <FaCalendarCheck />
                <span>
                  {formatDate(selectedDate)}
                  {selectedTime ? ` · ${selectedTime}` : ""}
                </span>
              </li>
            )}
          </ul>

          {fomoSettings?.enabled &&
            fomoSettings?.showAppointmentCounter &&
            appointmentCount && (
              <div className="cal-counter">
                🔥 Son 7 günde <strong>{appointmentCount}</strong> randevu alındı
              </div>
            )}
        </aside>

        <div className="cal-main">
          {step === "confirm" ? (
            <div className="cal-main-inner">
              <button
                className="cal-step-back"
                onClick={handleBackToTimeSelection}
              >
                <FaArrowLeft /> Saat seçimine dön
              </button>
              {renderConfirmation()}
            </div>
          ) : (
            <div className={`cal-picker ${selectedDate ? "has-times" : ""}`}>
              {renderCalendar()}
              {selectedDate && renderTimeSlots()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendlyCalendar;
